import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  X,
  Home,
  Calendar,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Loader2
} from 'lucide-react';

const AdminAvailabilitySyncDetails = ({ syncId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [expandedVillas, setExpandedVillas] = useState(new Set());
  const [villaBookings, setVillaBookings] = useState({});
  const [loadingVilla, setLoadingVilla] = useState(null);

  useEffect(() => {
    if (syncId) {
      loadDetails();
    }
  }, [syncId]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/availability/sync/${syncId}/details`);
      const data = await response.json();

      if (data.success) {
        setDetails(data.data);
      }
    } catch (error) {
      console.error('Error loading details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVillaBookings = async (villaId) => {
    if (villaBookings[villaId]) {
      // Déjà chargé, juste toggle
      toggleVilla(villaId);
      return;
    }

    setLoadingVilla(villaId);
    try {
      const response = await fetch(`/api/availability/sync/${syncId}/villa/${villaId}`);
      const data = await response.json();

      if (data.success) {
        setVillaBookings(prev => ({
          ...prev,
          [villaId]: data.data
        }));
        setExpandedVillas(prev => new Set([...prev, villaId]));
      }
    } catch (error) {
      console.error('Error loading villa bookings:', error);
    } finally {
      setLoadingVilla(null);
    }
  };

  const toggleVilla = (villaId) => {
    setExpandedVillas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(villaId)) {
        newSet.delete(villaId);
      } else {
        newSet.add(villaId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!syncId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Détails de la Synchronisation</h2>
            <p className="text-sm text-gray-600 mt-1">ID: {syncId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Chargement des détails...</span>
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Informations générales */}
              <Card className="p-4 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Type</p>
                    <p className="font-semibold text-gray-900">{details.sync.sync_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Villas Traitées</p>
                    <p className="font-semibold text-gray-900">
                      {details.sync.villas_processed}/{details.sync.villas_total}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Dates Mises à Jour</p>
                    <p className="font-semibold text-blue-600">{details.sync.dates_updated}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Nouvelles Réservations</p>
                    <p className="font-semibold text-green-600">{details.sync.dates_added}</p>
                  </div>
                </div>
              </Card>

              {/* Liste des villas */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-blue-600" />
                  Villas Synchronisées ({details.villas.length})
                </h3>

                <div className="space-y-2">
                  {details.villas.length === 0 ? (
                    <Card className="p-6 text-center text-gray-500">
                      Aucune villa avec des mises à jour trouvée
                    </Card>
                  ) : (
                    details.villas.map(villa => (
                      <Card key={villa.villa_id} className="overflow-hidden">
                        {/* En-tête de la villa */}
                        <button
                          onClick={() => loadVillaBookings(villa.villa_id)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            {expandedVillas.has(villa.villa_id) ? (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            )}
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">
                                #{villa.villa_id} - {villa.villa_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {villa.country} • {villa.zone}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Jours réservés</p>
                              <p className="font-semibold text-red-600">{villa.booked_days}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Jours disponibles</p>
                              <p className="font-semibold text-green-600">{villa.available_days}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Périodes</p>
                              <p className="font-semibold text-blue-600">
                                {villaBookings[villa.villa_id] ? villaBookings[villa.villa_id].length : '...'}
                              </p>
                            </div>
                            {loadingVilla === villa.villa_id && (
                              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            )}
                          </div>
                        </button>

                        {/* Détails des réservations (expansible) */}
                        {expandedVillas.has(villa.villa_id) && villaBookings[villa.villa_id] && (
                          <div className="p-4 bg-gray-50 border-t border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-600" />
                              Réservations ({villaBookings[villa.villa_id].bookings.length})
                            </h4>
                            
                            <div className="space-y-2">
                              {villaBookings[villa.villa_id].bookings.map((booking, idx) => (
                                <div
                                  key={idx}
                                  className="p-3 bg-white rounded-lg border border-gray-200"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <Badge className={
                                        booking.status === 'booked'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-green-100 text-green-800'
                                      }>
                                        {booking.status === 'booked' ? '🔴 Réservé' : '🟢 Disponible'}
                                      </Badge>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {formatDate(booking.check_in_date)} → {formatDate(booking.check_out_date)}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {booking.nights} nuit{booking.nights > 1 ? 's' : ''}
                                          {booking.min_stay && ` • Min: ${booking.min_stay} nuits`}
                                        </p>
                                      </div>
                                    </div>
                                    {booking.booking_ref && (
                                      <p className="text-xs text-gray-400 font-mono">
                                        {booking.booking_ref.substring(0, 12)}...
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Impossible de charger les détails
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAvailabilitySyncDetails;

